import { supabase } from '../lib/supabase';
import { Champion } from '../types/models';

// For champion rows from a past year we want the house the student was in
// during that year, not their current house. We do a second query against
// student_enrollments and overlay the historical house onto each champion.
const overlayEnrollmentHouses = async (champions: Champion[]): Promise<Champion[]> => {
  if (champions.length === 0) return champions;

  const pairs = champions
    .filter(c => c.student?.student_id && c.competition_year)
    .map(c => ({ student_id: c.student!.student_id!, year: c.competition_year }));

  if (pairs.length === 0) return champions;

  const studentIds = Array.from(new Set(pairs.map(p => p.student_id)));
  const years = Array.from(new Set(pairs.map(p => p.year)));

  const { data: enrollments, error } = await supabase
    .from('student_enrollments')
    .select(`
      student_id,
      competition_year,
      house:houses(house_id, house_name, house_color)
    `)
    .in('student_id', studentIds)
    .in('competition_year', years);

  if (error) {
    console.warn('Could not load enrollment houses for champions:', error.message);
    return champions;
  }

  const byKey = new Map<string, any>();
  (enrollments || []).forEach((e: any) => {
    byKey.set(`${e.student_id}-${e.competition_year}`, e.house);
  });

  return champions.map(c => {
    if (!c.student) return c;
    const enrolledHouse = byKey.get(`${c.student.student_id}-${c.competition_year}`);
    if (!enrolledHouse) return c;
    return { ...c, student: { ...c.student, house: enrolledHouse } };
  });
};

export const getChampions = async (): Promise<Champion[]> => {
  const { data, error } = await supabase
    .from('champions')
    .select(`
      *,
      student:students(
        student_id,
        first_name,
        last_name,
        first_name_tamil,
        last_name_tamil,
        date_of_birth,
        house:houses(
          house_id,
          house_name,
          house_color
        )
      )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return overlayEnrollmentHouses(data || []);
};

export const getChampionsByYear = async (year: number, gender?: string): Promise<Champion[]> => {
  let query = supabase
    .from('champions')
    .select(`
      *,
      student:students(
        student_id,
        first_name,
        last_name,
        first_name_tamil,
        last_name_tamil,
        date_of_birth,
        house:houses(
          house_id,
          house_name,
          house_color
        )
      )
    `)
    .eq('competition_year', year)
    .gte('categories_won', 3) // Ensure only champions with 3+ first place wins are returned
    .order('total_points', { ascending: false });

  if (gender) {
    query = query.eq('gender', gender === 'Male' ? 'M' : 'F');
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message);
  }

  return overlayEnrollmentHouses(data || []);
};

export const deleteChampion = async (id: string): Promise<void> => {
  // Check if user has admin role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators can delete champions');
  }

  const { error } = await supabase
    .from('champions')
    .delete()
    .eq('champion_id', id);
    
  if (error) {
    throw new Error(error.message);
  }
};

export const calculateChampions = async (year: number) => {
  try {
    // First, try to delete existing champions for the year to avoid conflicts
    await supabase
      .from('champions')
      .delete()
      .eq('competition_year', year);

    // Then recalculate champions
    const { data, error } = await supabase.rpc('calculate_champions_for_year', {
      year_param: year
    });

    if (error) {
      // If the RPC function doesn't exist or fails, fall back to manual calculation
      console.warn('RPC function failed, attempting manual calculation:', error);
      return await manualCalculateChampions(year);
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error calculating champions:', error);
    // Try manual calculation as fallback
    try {
      return await manualCalculateChampions(year);
    } catch (fallbackError) {
      console.error('Manual calculation also failed:', fallbackError);
      return { success: false, error: fallbackError };
    }
  }
};

// Manual champion calculation as fallback
const manualCalculateChampions = async (year: number) => {
  try {
    // Get all results for the year grouped by student and gender
    const { data: results, error: resultsError } = await supabase
      .from('competition_results')
      .select(`
        student_id,
        points_earned,
        position,
        student:students(
          gender,
          house_id
        )
      `)
      .eq('competition_year', year);

    if (resultsError) throw resultsError;

    // Group results by student and calculate totals
    const studentStats = new Map();
    
    results?.forEach(result => {
      const studentId = result.student_id;
      const gender = result.student?.gender;
      
      if (!studentStats.has(studentId)) {
        studentStats.set(studentId, {
          student_id: studentId,
          gender: gender,
          total_points: 0,
          categories_won: 0,
          competition_year: year
        });
      }
      
      const stats = studentStats.get(studentId);
      stats.total_points += result.points_earned || 0;
      
      // Count first place finishes as categories won
      if (result.position === 1) {
        stats.categories_won += 1;
      }
    });

    // Convert to array and filter for champions with at least 3 first place wins
    const championCandidates = Array.from(studentStats.values())
      .filter(candidate => candidate.categories_won >= 3) // Only include students with 3+ first place wins
      .sort((a, b) => b.total_points - a.total_points);

    // Use upsert instead of insert to handle conflicts gracefully
    if (championCandidates.length > 0) {
      const { error: upsertError } = await supabase
        .from('champions')
        .upsert(championCandidates, {
          onConflict: 'student_id,competition_year'
        });

      if (upsertError) throw upsertError;
    }

    return { success: true, data: championCandidates };
  } catch (error) {
    console.error('Manual champion calculation failed:', error);
    return { success: false, error };
  }
};

// New function to regenerate all champions
export const regenerateAllChampions = async () => {
  try {
    const { data, error } = await supabase.rpc('regenerate_all_champions');
    
    if (error) throw error;
    
    return { success: true, message: 'All champions regenerated successfully' };
  } catch (error) {
    console.error('Error regenerating all champions:', error);
    return { success: false, error };
  }
};

// New function to regenerate champions for a specific year
export const regenerateChampionsForYear = async (year: number) => {
  try {
    const { data, error } = await supabase.rpc('regenerate_champions_for_year', {
      year_param: year
    });
    
    if (error) throw error;
    
    return { success: true, message: `Champions for ${year} regenerated successfully` };
  } catch (error) {
    console.error(`Error regenerating champions for year ${year}:`, error);
    return { success: false, error };
  }
};